/**
 * 國際化 (i18n) 多語言支持
 * 支持繁體中文和英文
 */

export type Language = 'zh-TW' | 'en';

// ========================================
// 翻譯文本
// ========================================

export const translations = {
  'zh-TW': {
    // Logo 區塊
    'logo.title': 'EchoV',
    'logo.subtitle': 'Beta 測試版',
    'logo.description': 'BIM',
    
    // 控制面板
    'panel.title': '模型管理',
    'panel.globalControl': '全局控制',
    'panel.modelList': '模型列表',
    'panel.markerList': '標記列表',
    'panel.showAll': '全部顯示',
    'panel.hideAll': '全部隱藏',
    'panel.stats': '{visible} / {total} 個模型可見',
    'panel.markerStats': '{visible} / {total} 個標記可見',
    'panel.noMarkers': '尚未建立功能標記',
    
    // 按鈕
    'button.flyToModel': '飛去模型',
    'button.togglePanel': '收合/展開面板',
    'button.switchLanguage': '切換語言',
    'button.redQueenMode': 'Red Queen Mode',
    'button.on': 'ON',
    'button.off': 'OFF',
    
    // Folder 提示
    'folder.doubleClickToFly': '雙擊飛到位置',
    
    // 載入
    'loading.title': '載入多個模型中...',
    
    // 模型名稱（可選：如果要翻譯模型名稱）
    'model.school-arq-hk': '香港學校建築',
    'model.school-arq-barcelona': '巴塞羅那學校建築',
    'model.school-mep-hk': '香港學校機電',
    'model.school-str-hk': '香港學校結構',
    
    // 日誌訊息
    'log.modelLoaded': '模型加載完成',
    'log.modelShown': '顯示模型',
    'log.modelHidden': '隱藏模型',
    'log.modelNotFound': '找不到模型',
    
    // 地圖控制
    'map.controls.rotate': '旋轉地圖',
    'map.controls.zoom': '縮放地圖',
    'map.controls.pan': '平移地圖',
  },
  
  'en': {
    // Logo section
    'logo.title': 'EchoV',
    'logo.subtitle': 'Beta Test',
    'logo.description': 'BIM',
    
    // Control panel
    'panel.title': 'Model Management',
    'panel.globalControl': 'Global Control',
    'panel.modelList': 'Model List',
    'panel.markerList': 'Marker List',
    'panel.showAll': 'Show All',
    'panel.hideAll': 'Hide All',
    'panel.stats': '{visible} / {total} models visible',
    'panel.markerStats': '{visible} / {total} markers visible',
    'panel.noMarkers': 'No functional markers yet',
    
    // Buttons
    'button.flyToModel': 'Fly to Model',
    'button.togglePanel': 'Toggle Panel',
    'button.switchLanguage': 'Switch Language',
    'button.redQueenMode': 'Red Queen Mode',
    'button.on': 'ON',
    'button.off': 'OFF',
    
    // Folder hints
    'folder.doubleClickToFly': 'Double-click to fly',
    
    // Loading
    'loading.title': 'Loading multiple models...',
    
    // Model names (optional: if you want to translate model names)
    'model.school-arq-hk': 'School Architecture HK',
    'model.school-arq-barcelona': 'School Architecture Barcelona',
    'model.school-mep-hk': 'School MEP HK',
    'model.school-str-hk': 'School Structure HK',
    
    // Log messages
    'log.modelLoaded': 'Model loaded',
    'log.modelShown': 'Show model',
    'log.modelHidden': 'Hide model',
    'log.modelNotFound': 'Model not found',
    
    // Map controls
    'map.controls.rotate': 'Rotate map',
    'map.controls.zoom': 'Zoom map',
    'map.controls.pan': 'Pan map',
  }
};

// ========================================
// i18n 類別
// ========================================

export class I18n {
  private currentLanguage: Language;
  private translations: typeof translations;
  
  constructor(defaultLanguage: Language = 'en') {
    this.currentLanguage = defaultLanguage;
    this.translations = translations;
    
    // 自動遷移舊的語言設定（robim -> echov）
    const oldLanguage = localStorage.getItem('robim-language');
    if (oldLanguage && !localStorage.getItem('echov-language')) {
      localStorage.setItem('echov-language', oldLanguage);
      console.log('✅ 已自動遷移語言設定：robim-language -> echov-language');
    }
    
    // 從 localStorage 讀取保存的語言設定
    const savedLanguage = localStorage.getItem('echov-language') as Language;
    if (savedLanguage && (savedLanguage === 'zh-TW' || savedLanguage === 'en')) {
      this.currentLanguage = savedLanguage;
    }
  }
  
  /**
   * 獲取當前語言
   */
  getCurrentLanguage(): Language {
    return this.currentLanguage;
  }
  
  /**
   * 設置語言
   */
  setLanguage(language: Language): void {
    this.currentLanguage = language;
    localStorage.setItem('echov-language', language);
    console.log(`🌐 語言已切換為: ${language}`);
  }
  
  /**
   * 切換語言
   */
  toggleLanguage(): Language {
    const newLanguage = this.currentLanguage === 'zh-TW' ? 'en' : 'zh-TW';
    this.setLanguage(newLanguage);
    return newLanguage;
  }
  
  /**
   * 翻譯文本
   * @param key 翻譯鍵值
   * @param params 參數對象（用於替換 {變數}）
   */
  t(key: string, params?: Record<string, any>): string {
    let text = this.translations[this.currentLanguage][key] || key;
    
    // 替換參數
    if (params) {
      Object.keys(params).forEach(param => {
        text = text.replace(`{${param}}`, String(params[param]));
      });
    }
    
    return text;
  }
  
  /**
   * 翻譯模型名稱
   * 如果當前語言沒有翻譯，則返回原始名稱
   */
  translateModelName(modelId: string, originalName: string): string {
    const key = `model.${modelId}`;
    const currentLangTranslations = this.translations[this.currentLanguage];
    
    // 如果當前語言有該模型的翻譯，使用翻譯
    if (currentLangTranslations[key]) {
      return currentLangTranslations[key];
    }
    
    // 否則返回原始名稱（保持英文）
    return originalName;
  }
}

// ========================================
// 全局 i18n 實例
// ========================================

export const i18n = new I18n();

// 暴露到全局（方便調試）
if (typeof window !== 'undefined') {
  (window as any).i18n = i18n;
}
